import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import Placeholder from "@tiptap/extension-placeholder"
import BubbleMenu from "@tiptap/extension-bubble-menu"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { TableHeader } from "@tiptap/extension-table-header"
import { TableCell } from "@tiptap/extension-table-cell"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { createLowlight } from "lowlight"
import javascript from "highlight.js/lib/languages/javascript"
import typescript from "highlight.js/lib/languages/typescript"
import php from "highlight.js/lib/languages/php"
import ruby from "highlight.js/lib/languages/ruby"
import xml from "highlight.js/lib/languages/xml"
import css from "highlight.js/lib/languages/css"
import json from "highlight.js/lib/languages/json"
import bash from "highlight.js/lib/languages/bash"
import sql from "highlight.js/lib/languages/sql"
import yaml from "highlight.js/lib/languages/yaml"
import python from "highlight.js/lib/languages/python"

// Resaltado de sintaxis (tipo VSCode) para bloques de código.
const lowlight = createLowlight()
lowlight.register({
  javascript, js: javascript, typescript, ts: typescript,
  php, ruby, rb: ruby, xml, html: xml, css, json,
  bash, sh: bash, sql, yaml, yml: yaml, python, py: python,
})

// Editor de texto enriquecido estilo Medium.
// - Bubble menu (al seleccionar): negrita, cursiva, enlace, H2, H3, cita, lista.
// - Barra de inserción (bloques): imagen (con subida), tabla, línea, deshacer/rehacer.
export default class extends Controller {
  static targets = ["input", "editor"]
  static values = {
    placeholder:  { type: String, default: "Escribe algo… selecciona texto para dar formato." },
    uploadUrl:    { type: String, default: "" },
    gifSearchUrl: { type: String, default: "" },
  }

  connect() {
    this.bubble = this.buildBubble()
    this.toolbar = this.buildToolbar()

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false }),
        CodeBlockLowlight.configure({ lowlight }),
        Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-accent-dark underline" } }),
        Image.configure({ HTMLAttributes: { class: "rounded-lg" } }),
        Placeholder.configure({ placeholder: this.placeholderValue }),
        Table.configure({ resizable: true }),
        TableRow, TableHeader, TableCell,
        BubbleMenu.configure({ element: this.bubble, options: { placement: "top", offset: 8 } }),
      ],
      content: this.inputTarget.value || "",
      editorProps: {
        attributes: { class: "tiptap-body prose max-w-none min-h-[280px] focus:outline-none" },
      },
      onUpdate: ({ editor }) => this.syncInput(editor),
      onSelectionUpdate: () => this.refreshBubble(),
      onTransaction: () => this.refreshBubble(),
    })
    // Red de seguridad: sincroniza justo antes de enviar el formulario.
    this.form = this.element.closest("form")
    if (this.form) {
      this.onSubmit = () => this.syncInput(this.editor)
      this.form.addEventListener("submit", this.onSubmit)
    }
  }

  // Vuelca el HTML del editor al input oculto. No usa editor.isEmpty porque
  // devuelve true con tablas/imágenes (que no tienen texto).
  syncInput(editor) {
    const html = editor.getHTML()
    this.inputTarget.value = html === "<p></p>" ? "" : html
  }

  disconnect() {
    if (this.editor) this.editor.destroy()
    if (this.bubble) this.bubble.remove()
    if (this.toolbar) this.toolbar.remove()
    if (this.gifModal) {
      this.gifModal.overlay.remove()
      if (this.onGifKey) document.removeEventListener("keydown", this.onGifKey)
    }
  }

  // ---- Barra de inserción (bloques) ----
  buildToolbar() {
    const bar = document.createElement("div")
    bar.className = "tiptap-toolbar"
    const items = [
      this.uploadUrlValue ? { label: "🖼️ Imagen", title: "Insertar imagen", run: () => this.insertImage() } : null,
      this.gifSearchUrlValue ? { label: "GIF", title: "Insertar GIF", run: () => this.openGifPicker() } : null,
      { label: "▦ Tabla",  title: "Insertar tabla",  run: () => this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
      { label: "</> Código", title: "Bloque de código", run: () => this.editor.chain().focus().toggleCodeBlock().run() },
      { label: "―",         title: "Línea",           run: () => this.editor.chain().focus().setHorizontalRule().run() },
      { sep: true },
      { label: "↶", title: "Deshacer", run: () => this.editor.chain().focus().undo().run() },
      { label: "↷", title: "Rehacer",  run: () => this.editor.chain().focus().redo().run() },
    ].filter(Boolean)
    items.forEach((it) => {
      if (it.sep) { const s = document.createElement("span"); s.className = "tiptap-toolbar-sep"; bar.appendChild(s); return }
      const b = document.createElement("button")
      b.type = "button"; b.title = it.title; b.textContent = it.label
      b.className = "tiptap-toolbar-btn"
      b.addEventListener("click", (e) => { e.preventDefault(); it.run() })
      bar.appendChild(b)
    })
    this.editorTarget.before(bar)
    return bar
  }

  insertImage() {
    const input = document.createElement("input")
    input.type = "file"; input.accept = "image/*"
    input.onchange = async () => {
      const file = input.files && input.files[0]
      if (!file) return
      try {
        const fd = new FormData(); fd.append("file", file)
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        const res = await fetch(this.uploadUrlValue, { method: "POST", headers: token ? { "X-CSRF-Token": token } : {}, body: fd })
        const data = await res.json()
        if (data.url) this.editor.chain().focus().setImage({ src: data.url }).run()
        else alert(data.error || "No se pudo subir la imagen.")
      } catch (e) { alert("Error subiendo la imagen.") }
    }
    input.click()
  }

  // ---- Buscador de GIFs (Giphy) ----
  openGifPicker() {
    if (!this.gifModal) this.gifModal = this.buildGifModal()
    this.gifModal.overlay.style.display = "flex"
    this.gifModal.input.value = ""
    this.gifModal.input.focus()
    this.loadGifs("")
  }

  buildGifModal() {
    const overlay = document.createElement("div")
    overlay.className = "tiptap-gif-overlay"
    overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) this.closeGifPicker() })

    const modal = document.createElement("div")
    modal.className = "tiptap-gif-modal"

    const header = document.createElement("div")
    header.className = "tiptap-gif-header"
    const input = document.createElement("input")
    input.type = "text"; input.placeholder = "Buscar GIFs…"; input.className = "tiptap-gif-input"
    let timer
    input.addEventListener("input", () => { clearTimeout(timer); timer = setTimeout(() => this.loadGifs(input.value.trim()), 350) })
    const close = document.createElement("button")
    close.type = "button"; close.textContent = "✕"; close.className = "tiptap-gif-close"
    close.addEventListener("click", () => this.closeGifPicker())
    header.appendChild(input); header.appendChild(close)

    const grid = document.createElement("div")
    grid.className = "tiptap-gif-grid"

    const footer = document.createElement("div")
    footer.className = "tiptap-gif-footer"; footer.textContent = "Powered by GIPHY"

    modal.appendChild(header); modal.appendChild(grid); modal.appendChild(footer)
    overlay.appendChild(modal)
    document.body.appendChild(overlay)

    this.onGifKey = (e) => { if (e.key === "Escape") this.closeGifPicker() }
    document.addEventListener("keydown", this.onGifKey)

    return { overlay, input, grid }
  }

  async loadGifs(q) {
    const grid = this.gifModal.grid
    grid.innerHTML = '<p class="tiptap-gif-status">Cargando…</p>'
    try {
      const url = new URL(this.gifSearchUrlValue, window.location.origin)
      if (q) url.searchParams.set("q", q)
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await res.json()
      grid.innerHTML = ""
      const gifs = data.gifs || []
      if (!gifs.length) { grid.innerHTML = '<p class="tiptap-gif-status">Sin resultados</p>'; return }
      gifs.forEach((g) => {
        const img = document.createElement("img")
        img.src = g.preview; img.alt = g.alt || "GIF"; img.loading = "lazy"; img.className = "tiptap-gif-item"
        img.addEventListener("click", () => {
          this.editor.chain().focus().setImage({ src: g.full, alt: g.alt || "GIF" }).run()
          this.closeGifPicker()
        })
        grid.appendChild(img)
      })
    } catch (e) {
      grid.innerHTML = '<p class="tiptap-gif-status">No se pudieron cargar los GIFs</p>'
    }
  }

  closeGifPicker() {
    if (this.gifModal) this.gifModal.overlay.style.display = "none"
  }

  // ---- Bubble menu (inline) ----
  buildBubble() {
    const el = document.createElement("div")
    el.className = "tiptap-bubble"
    el.style.visibility = "hidden"
    this.btns = [
      { key: "bold",   label: "B",  title: "Negrita",  cls: "font-bold", run: (e) => e.chain().focus().toggleBold().run(),   active: (e) => e.isActive("bold") },
      { key: "italic", label: "I",  title: "Cursiva",  cls: "italic",    run: (e) => e.chain().focus().toggleItalic().run(), active: (e) => e.isActive("italic") },
      { key: "code",   label: "‹›", title: "Código",   run: (e) => e.chain().focus().toggleCode().run(), active: (e) => e.isActive("code") },
      { key: "link",   label: "🔗", title: "Enlace",   run: (e) => this.setLink(e), active: (e) => e.isActive("link") },
      { sep: true },
      { key: "h2",     label: "H2", title: "Título",    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(), active: (e) => e.isActive("heading", { level: 2 }) },
      { key: "h3",     label: "H3", title: "Subtítulo", run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(), active: (e) => e.isActive("heading", { level: 3 }) },
      { key: "quote",  label: "“”", title: "Cita",      run: (e) => e.chain().focus().toggleBlockquote().run(), active: (e) => e.isActive("blockquote") },
      { key: "bullet", label: "•",  title: "Lista",     run: (e) => e.chain().focus().toggleBulletList().run(), active: (e) => e.isActive("bulletList") },
    ]
    this.btns.forEach((b) => {
      if (b.sep) { const s = document.createElement("span"); s.className = "tiptap-bubble-sep"; el.appendChild(s); return }
      const btn = document.createElement("button")
      btn.type = "button"; btn.title = b.title; btn.textContent = b.label
      btn.className = `tiptap-bubble-btn ${b.cls || ""}`
      btn.addEventListener("mousedown", (e) => { e.preventDefault(); b.run(this.editor); this.refreshBubble() })
      b.el = btn
      el.appendChild(btn)
    })
    this.element.appendChild(el)
    return el
  }

  refreshBubble() {
    if (!this.editor || !this.btns) return
    this.btns.forEach((b) => {
      if (b.sep || !b.el) return
      b.el.classList.toggle("is-active", !!(b.active && b.active(this.editor)))
    })
  }

  setLink(editor) {
    const previous = editor.getAttributes("link").href
    const url = window.prompt("URL del enlace:", previous || "https://")
    if (url === null) return
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }
}
